// MCP Personal Access Tokens panel — list / create / revoke.
// Aligns PP main #135. PP-CF specifics: tokens authenticate against
// the Worker directly via Authorization: Bearer pp_mcp_…; no Arx
// refresh chain.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Key, Plus, Trash, Copy, Check, SpinnerGap, X,
} from '@phosphor-icons/react';
import { apiFetch } from '../../api/auth-fetch';

interface PublicMcpToken {
  id: string;
  name: string;
  token_prefix: string;
  last_used_at: number | null;
  expires_at: number | null;
  revoked_at: number | null;
  created_at: number;
}

type ExpiryChoice = 'never' | '30d' | '90d' | '1y';

function formatTs(ts: number | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatLastUsed(ts: number | null): string {
  if (!ts) return 'never';
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return formatTs(ts);
}

export default function McpTokensPanel() {
  const [tokens, setTokens] = useState<PublicMcpToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [expires, setExpires] = useState<ExpiryChoice>('never');
  const [reveal, setReveal] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await apiFetch('/api/auth/mcp-tokens');
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error?.message || 'load failed');
      setTokens(d?.tokens ?? []);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      const r = await apiFetch('/api/auth/mcp-tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), expires }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error?.message || 'create failed');
      setReveal(d.raw);
      setName('');
      setExpires('never');
      setShowCreate(false);
      void load();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string) => {
    setRevokingId(id);
    setError('');
    try {
      const r = await apiFetch(`/api/auth/mcp-tokens/${id}`, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        throw new Error(d?.error?.message || 'revoke failed');
      }
      void load();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setRevokingId(null);
      setConfirmId(null);
    }
  };

  const handleCopy = async (raw: string) => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback path: prompt() so the user can still copy.
      window.prompt('Copy your token:', raw);
    }
  };

  const sectionStyle: React.CSSProperties = {
    border: '1px solid var(--border)',
    borderRadius: 12,
    background: 'var(--bg-surface)',
    padding: '20px 24px',
    marginTop: 24,
  };

  const btnPrimary: React.CSSProperties = {
    padding: '6px 14px',
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
    background: 'var(--brand)',
    color: 'var(--text-on-brand)',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const btnGhost: React.CSSProperties = {
    padding: '6px 14px',
    fontSize: 'var(--fs-sm)',
    fontWeight: 500,
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h3 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Key size={16} weight="light" /> MCP Tokens
        </h3>
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>
          <Plus size={12} weight="bold" /> New token
        </button>
      </div>
      <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', marginTop: 0, marginBottom: 16 }}>
        Personal Access Tokens for external MCP clients (Claude Desktop,
        Cursor, custom agents). Send as <code>Authorization: Bearer pp_mcp_…</code>.
        The raw token is shown <strong>once</strong> at creation — store it then;
        you can't read it back.
      </p>

      {error && (
        <div style={{ padding: '8px 12px', background: 'var(--ds-danger-soft)', border: '1px solid var(--ds-danger-soft)', borderRadius: 8, color: 'var(--ds-danger)', fontSize: 'var(--fs-sm)', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* One-time reveal */}
      {reveal && (
        <div style={{ padding: 14, background: 'var(--ds-success-soft)', border: '1px solid var(--ds-success-soft)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
              Token created — copy it now. You won't see it again.
            </span>
            <button onClick={() => setReveal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
              <X size={14} weight="light" />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <code style={{ flex: 1, padding: '8px 10px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 6, fontFamily: 'monospace', fontSize: 'var(--fs-xs)', overflow: 'auto', whiteSpace: 'nowrap' }}>
              {reveal}
            </code>
            <button onClick={() => handleCopy(reveal)} style={btnGhost}>
              {copied ? <><Check size={12} weight="bold" /> Copied</> : <><Copy size={12} weight="light" /> Copy</>}
            </button>
          </div>
        </div>
      )}

      {/* Create modal — inline */}
      {showCreate && (
        <div style={{ padding: 14, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 16 }}>
          <div style={{ marginBottom: 10 }}>
            <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Claude Desktop on laptop"
              maxLength={100}
              autoFocus
              style={{ width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Expires</label>
            <select
              value={expires}
              onChange={(e) => setExpires(e.target.value as ExpiryChoice)}
              style={{ padding: '8px 12px', fontSize: 'var(--fs-base)', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'inherit' }}
            >
              <option value="never">Never</option>
              <option value="30d">30 days</option>
              <option value="90d">90 days</option>
              <option value="1y">1 year</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={() => { setShowCreate(false); setName(''); }} style={btnGhost}>
              Cancel
            </button>
            <button onClick={create} disabled={creating || !name.trim()} style={{ ...btnPrimary, opacity: creating || !name.trim() ? 0.6 : 1 }}>
              {creating ? <SpinnerGap size={12} weight="light" className="animate-spin" /> : null}
              Create
            </button>
          </div>
        </div>
      )}

      {/* Token list */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
          <SpinnerGap size={14} weight="light" className="animate-spin" /> Loading…
        </div>
      ) : tokens.length === 0 ? (
        <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', padding: 16, textAlign: 'center', border: '1px dashed var(--border)', borderRadius: 8 }}>
          No tokens yet. Click "New token" to mint one.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr style={{ background: 'var(--bg-primary)', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Name</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Token</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Last used</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Expires</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const active = !t.revoked_at && (!t.expires_at || t.expires_at > Date.now());
                return (
                  <tr key={t.id} style={{ borderBottom: '1px solid var(--border)', opacity: active ? 1 : 0.5 }}>
                    <td style={{ padding: '10px 14px', color: 'var(--text-primary)' }}>{t.name}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)' }}>{t.token_prefix}…</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{formatLastUsed(t.last_used_at)}</td>
                    <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>
                      {t.revoked_at ? <span style={{ color: 'var(--ds-danger)' }}>revoked {formatTs(t.revoked_at)}</span>
                       : t.expires_at && t.expires_at < Date.now() ? <span style={{ color: 'var(--ds-warning)' }}>expired {formatTs(t.expires_at)}</span>
                       : formatTs(t.expires_at)}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                      {active && (
                        confirmId === t.id ? (
                          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>Sure?</span>
                            <button
                              onClick={() => revoke(t.id)}
                              disabled={revokingId === t.id}
                              style={{ padding: '4px 10px', fontSize: 'var(--fs-xs)', fontWeight: 600, background: 'var(--ds-danger-soft)', color: 'var(--ds-danger)', border: '1px solid var(--ds-danger-soft)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}
                            >
                              {revokingId === t.id ? <SpinnerGap size={10} className="animate-spin" /> : 'Revoke'}
                            </button>
                            <button onClick={() => setConfirmId(null)} style={{ padding: '4px 10px', fontSize: 'var(--fs-xs)', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit' }}>
                              No
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setConfirmId(t.id)}
                            title="Revoke this token"
                            style={{ padding: '4px 8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center' }}
                          >
                            <Trash size={12} weight="light" />
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
